/** Mini day-progress dots for Today Battlefield view — dots only, no StatBlock wrapper. */
import { useMemo } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import { useTasksContext } from '@/features/tasks';
import { getDayCapacityMinutes } from '@/features/tasks/utils/scheduling/taskSchedulingUtils';
import { DAY_PROGRESS_DOTS_FRAME_SX } from './dayProgressDotsFrame';

const SLOT_MINS = 30;
const DOTS_PER_ROW = 8; // More compact for mini view
const DOT_DIAMETER = 16; // Smaller dots for mini
const DOT_GAP = '2px';
const HALF_DOT_BORDER_PX = 1;
const HALF_SLOT_FRACTION = 0.5;
const FLOAT_EPSILON = 0.01;

const toLocalDateStr = (val) => {
  if (!val) return null;
  const date =
    val instanceof Date ? val : val.toDate ? val.toDate() : new Date(val);
  if (Number.isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const DayProgressDotsMinimal = ({ sx }) => {
  const { allTasks, tasksMap } = useTasksContext();
  const theme = useTheme();
  const completedColor =
    theme.palette.secondary?.main ?? theme.palette.grey?.[600] ?? '#7C3AED';
  const incompleteBorder =
    theme.palette.neutral?.border ?? theme.palette.divider ?? '#cccccc';

  const { totalCells, completedMins, totalMins } = useMemo(() => {
    const todayStr = toLocalDateStr(new Date());
    const map = tasksMap ?? new Map();
    const todayTasks = (allTasks ?? []).filter((task) => {
      if (task.archived) return false;
      return toLocalDateStr(task.date) === todayStr;
    });
    const { plannedMins: total, completedMins: completed } =
      getDayCapacityMinutes(todayTasks, map);

    return {
      totalCells: Math.ceil(total / SLOT_MINS),
      totalMins: total,
      completedMins: completed,
    };
  }, [allTasks, tasksMap]);

  if (totalCells === 0) return null;

  const rowCount = Math.ceil(totalCells / DOTS_PER_ROW);

  const renderDot = (i) => {
    const scheduledFrac = Math.min(1, Math.max(0, totalMins / SLOT_MINS - i));
    const completedFrac = Math.min(
      1,
      Math.max(0, completedMins / SLOT_MINS - i),
    );
    const isHalfSlot = scheduledFrac > 0 && scheduledFrac < 1;
    const isDone = completedFrac >= 1;
    const isHalfDone = completedFrac >= HALF_SLOT_FRACTION && !isDone;

    if (isHalfSlot) {
      return (
        <Box
          key={i}
          sx={{
            width: DOT_DIAMETER,
            height: DOT_DIAMETER,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            data-testid="d-shape-fill"
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '50%',
              height: '100%',
              borderRadius: '100px 0 0 100px',
              bgcolor:
                completedFrac >= scheduledFrac - FLOAT_EPSILON &&
                scheduledFrac > 0
                  ? completedColor
                  : 'transparent',
              border: `${HALF_DOT_BORDER_PX}px solid ${incompleteBorder}`,
              borderRight: 'none',
              boxSizing: 'border-box',
            }}
          />
        </Box>
      );
    }

    return (
      <Box
        key={i}
        sx={{
          width: DOT_DIAMETER,
          height: DOT_DIAMETER,
          borderRadius: '50%',
          background: isDone
            ? completedColor
            : isHalfDone
              ? `linear-gradient(to right, ${completedColor} 50%, transparent 50%)`
              : 'transparent',
          border: `1px solid ${isDone ? completedColor : incompleteBorder}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.2s ease, border-color 0.2s ease',
        }}
      >
        {isDone ? (
          <CheckIcon
            sx={{ fontSize: '70%', color: 'white', display: 'block' }}
          />
        ) : null}
      </Box>
    );
  };

  return (
    <Box
      data-testid="day-progress-dots-frame"
      sx={{ ...DAY_PROGRESS_DOTS_FRAME_SX, ...sx }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          width: '100%',
        }}
      >
        {Array.from({ length: rowCount }, (_, rowIndex) => (
          <Box
            key={rowIndex}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: DOT_GAP,
              width: '100%',
              minHeight: DOT_DIAMETER,
            }}
          >
            {Array.from({ length: DOTS_PER_ROW }, (_, colIndex) => {
              const i = rowIndex * DOTS_PER_ROW + colIndex;
              if (i >= totalCells)
                return (
                  <Box
                    key={colIndex}
                    sx={{
                      width: DOT_DIAMETER,
                      height: DOT_DIAMETER,
                      flexShrink: 0,
                      visibility: 'hidden',
                    }}
                  />
                );
              return renderDot(i);
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default DayProgressDotsMinimal;
